import React from 'react';
import { Order } from '../../lib/types';
import { formatCurrency } from '../../lib/utils';
import type { OrderAuditEntry } from '../../lib/api';

interface ExpandedRowContentProps {
  order: Order;
  currency: string;
  locale: string;
  auditOpen: boolean;
  auditLoading: boolean;
  auditLog: OrderAuditEntry[];
  onToggleAudit: () => void;
}

export function ExpandedRowContent({
  order, currency, locale,
  auditOpen, auditLoading, auditLog, onToggleAudit,
}: ExpandedRowContentProps) {
  const items = order.order_items ?? [];
  const subtotal = items.reduce((s, i) => s + ((i.unit_price || 0) * (i.quantity || 1)), 0);
  const total = order.total_cost || 0;
  const gst = total - subtotal;

  return (
    <div
      style={{
        background: 'var(--surface2)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-4) var(--sp-5)',
        marginTop: 2,
      }}
    >
      {/* meta row */}
      <div className="flex flex-wrap gap-5 mb-3" style={{ fontSize: 'var(--fs-md)' }}>
        {order.staff_name && (
          <div>
            <span className="text-muted font-medium">Ordered for:</span>{' '}
            <span className="font-bold">{order.staff_name}</span>
          </div>
        )}
        {order.category && (
          <div>
            <span className="text-muted font-medium">Category:</span>{' '}
            <span className="font-bold">{order.category}</span>
          </div>
        )}
        {order.notes && (
          <div>
            <span className="text-muted font-medium">Notes:</span>{' '}
            <span>{order.notes}</span>
          </div>
        )}
        {order.document_type && order.document_type !== 'order_confirmation' && (
          <div>
            <span className="text-muted font-medium">Doc type:</span>{' '}
            <span className="font-bold" style={{ textTransform: 'capitalize' }}>
              {order.document_type.replace(/_/g, ' ')}
            </span>
          </div>
        )}
        {order.expected_delivery && (
          <div>
            <span className="text-muted font-medium">Expected delivery:</span>{' '}
            <span className="font-bold">{order.expected_delivery}</span>
          </div>
        )}
      </div>

      {/* items */}
      {items.map((item, i, arr) => {
        const qty = item.quantity || 1;
        const price = item.unit_price || 0;
        return (
          <div
            key={item.id || i}
            className="flex items-baseline gap-2"
            style={{
              padding: '6px 0',
              borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
            }}
          >
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium">{item.item_name || '—'}</span>
              {(item.code || item.size) && (
                <span className="text-xs text-subtle ml-2">
                  {item.code ? item.code : ''}
                  {item.code && item.size ? ' · ' : ''}
                  {item.size ? item.size : ''}
                </span>
              )}
            </div>
            <span className="text-sm text-muted flex-none" style={{ width: 36, textAlign: 'right' }}>×{qty}</span>
            <span className="text-xs text-mono text-subtle flex-none" style={{ width: 80, textAlign: 'right' }}>
              {price > 0 ? `${formatCurrency(price, currency)} ea` : ''}
            </span>
            <span className="text-sm text-mono font-semibold flex-none" style={{ width: 72, textAlign: 'right' }}>
              {price > 0 ? formatCurrency(qty * price, currency) : ''}
            </span>
          </div>
        );
      })}

      {/* subtotal / gst / total */}
      {total > 0 && (
        <div className="flex items-center gap-5 justify-end mt-2" style={{ paddingTop: 'var(--sp-2)' }}>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted">Subtotal</span>
            <span className="text-sm text-mono font-semibold">{formatCurrency(subtotal, currency)}</span>
          </div>
          {gst > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted">GST</span>
              <span className="text-sm text-mono font-semibold">{formatCurrency(gst, currency)}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted">Total</span>
            <span className="text-sm text-mono font-semibold">{formatCurrency(total, currency)}</span>
          </div>
        </div>
      )}

      {/* audit log */}
      <div className="mt-3" style={{ paddingTop: 'var(--sp-2)', borderTop: '1px solid var(--border)' }}>
        <button
          className="btn-ghost"
          onClick={onToggleAudit}
          style={{ background: 'none', border: 'none', padding: 0, fontSize: 'var(--fs-xs)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}
        >
          {auditOpen ? 'Hide history ▴' : 'History ▾'}
        </button>
        {auditOpen && (
          <div className="mt-2">
            {auditLoading ? (
              <div className="text-xs text-subtle">Loading…</div>
            ) : auditLog.length === 0 ? (
              <div className="text-xs text-subtle">No history recorded yet</div>
            ) : (
              <div className="flex flex-col gap-2">
                {auditLog.map(entry => (
                  <div key={entry.id} className="flex gap-2 items-baseline text-xs">
                    <span className="text-mono text-subtle flex-none" style={{ fontSize: 10 }}>
                      {new Date(entry.created_at).toLocaleDateString(locale, { day: '2-digit', month: 'short' })}{' '}
                      {new Date(entry.created_at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="text-muted">
                      <AuditMessage entry={entry} />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AuditMessage({ entry }: { entry: OrderAuditEntry }) {
  const name = <strong>{entry.performed_by_name}</strong>;
  switch (entry.action) {
    case 'created':
      return <>{name} created this order{entry.new_value ? ` — ${entry.new_value}` : ''}</>;
    case 'status_change':
      return <>{name} changed status from <span className="font-medium">{entry.old_value}</span> to <span className="font-medium">{entry.new_value}</span></>;
    case 'field_update':
      return <>{name} updated {entry.field_name}{entry.old_value ? ` from "${entry.old_value}"` : ''} to "{entry.new_value}"</>;
    case 'deleted':
      return <>{name} deleted this order</>;
    case 'restored':
      return <>{name} restored this order</>;
    default:
      return <>{name} {entry.action}</>;
  }
}
